import apiArtifacts from '.spec/self-care/apiArtifacts';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import apolloLogger from 'apollo-link-logger';
import { debounce } from 'lodash';
import { filter, isEmpty } from 'lodash/fp';
import store from 'store2';

import { LANGUAGE } from '@skippay/core/constants/queryAndStorageKeys';
import { captureException } from '@skippay/core/global/errorTracking';
import { selectionSetToFragment, wrapInDocument } from '@skippay/core/utils/gqlAstUtils';
import {
	createApolloLink,
	createAxiosHttpAgent,
	possibleTypesFromApiArtifacts,
} from '@skippay/core/utils/gqlEngineUtils';
import { memoPendingRequestsMiddleware } from '@skippay/core/utils/gqlMemoPendingRequests';
import { xFieldsRequestMiddleware } from '@skippay/core/utils/gqlToXFields';
import { gqlXLanguageMiddleware } from '@skippay/core/utils/gqlToXLanguage';
import { mergeFnPaginatedData } from '@skippay/core/utils/gqlUtils';
import { oasValidationMiddleware } from '@skippay/core/utils/gqlValidator';
import matchesFilter from '@skippay/core/utils/matchesFilter';
import { Widget } from '@skippay/widget';

import AccountAuthorizationFragment from 'fragments/AccountAuthorizationFragment.graphql';
import AmountFragment from 'fragments/AmountFragment.graphql';
import BillingDefermentFragment from 'fragments/BillingDefermentFragment.graphql';
import BillingFragment from 'fragments/BillingFragment.graphql';
import PromocodeOfferFragment from 'fragments/PromocodeOfferFragment.graphql';
import { getAuthorizationApolloLink } from 'modules/auth/utils/accessTokenUtils';

import resolvers from './resolvers';

const ssr = typeof window === `undefined`;

/**
 * This typePolicy defines a singleton entity - one such only single one ever exists in out application.
 * @type {{keyFields: *[], merge: boolean}}
 */
const singletonTypePolicy = {
	// the singleton has no keyFields
	keyFields: [],
	// whenever the entity returns from the BE, merge its new result with old data
	merge: true,
};

function debouncedWidgetUpdate() {
	debounce(Widget?.reloadWidget(), 2000);
}

/**
 * @param {{ accessToken?: string }} [config]
 * @return {import(`@apollo/client`).ApolloClient}
 */
export const createApolloClient = (config) => {
	const language = store.local.get(LANGUAGE) === 'sk' ? 'SK' : 'CZ';

	const cache = new InMemoryCache({
		resultCacheMaxSize: 20000,
		typePolicies: {
			Billing_additionalInfoDeferment: {
				merge: true,
			},
			Contract: singletonTypePolicy,
			coinsOverview: singletonTypePolicy,
			InvestmentsAccountResponse: singletonTypePolicy,
			NotificationSettings: singletonTypePolicy,
			Billing: {
				fields: {
					paymentAvailability: {
						merge(existing, incoming) {
							return {
								...existing,
								...incoming,
								reasons: incoming?.reasons || existing?.reasons,
							};
						},
					},
				},
			},
			PagingInfo: {
				keyFields: false,
			},
			Query: {
				fields: {
					accountAuthorizations: {
						read(cached, { args, readField }) {
							// The client `matchesFilter` is necessary here as data might be updated by some other resources. The
							// listing holds all data received from the server, the data is normalized and connected with other
							// resources via identity (__typename + id), so the changes of the data caused by other resources are
							// propagated, but this field does not know, that cached authorization should not be listed anymore
							// when its state changed from `WAITING` to e.g. `GRANTED` while the listing filter is set to
							// `state|eq|WAITING` - this missing knowledge we must fill in here.
							if (!args?.filter || isEmpty(cached?.data)) {
								return cached;
							}
							return {
								...cached,
								data: filter(matchesFilter(args.filter, { readField }), cached.data),
							};
						},
					},
					accountAuthorization(cached, { args: { id } }) {
						return (
							cached ||
							cache.readFragment({
								id: `AuthorizationResponse:${id}`,
								fragment: AccountAuthorizationFragment,
								fragmentName: `AccountAuthorizationFragment`,
							}) ||
							undefined
						);
					},
					coinsTransactions: {
						keyArgs: [`filter`, `sort`],
						merge: mergeFnPaginatedData,
					},
					couponsList: {
						keyArgs: ['filter', 'sort', 'limit'],
						merge: mergeFnPaginatedData,
					},
					myContractDirectDebit: {
						keyArgs: [`filter`, `sort`, `limit`],
						merge: mergeFnPaginatedData,
					},
					myContractApplications: {
						keyArgs: [`filter`, `sort`, `limit`],
						merge: mergeFnPaginatedData,
					},
					myContractBillings: {
						keyArgs: [`filter`, `sort`, `limit`],
						merge: mergeFnPaginatedData,
					},
					myDocuments: {
						keyArgs: ['type', `filter`],
						merge: mergeFnPaginatedData,
					},
					myInvestmentsAccount: {
						keyArgs: [`filter`, `sort`],
						merge: mergeFnPaginatedData,
					},
					myInvestmentsAccountTransactions: {
						keyArgs: [`filter`, `sort`],
						merge: mergeFnPaginatedData,
					},
					myInstallmentsContracts: {
						keyArgs: [`filter`, `sort`, `limit`],
						merge: mergeFnPaginatedData,
					},
					myContractCards: {
						keyArgs: [`filter`],
						merge: mergeFnPaginatedData,
					},
					myContractPaymentRecipients: {
						keyArgs: [`filter`, `sort`, `limit`],
						merge: mergeFnPaginatedData,
					},
					myContractTransactions: {
						keyArgs: [`filter`],
						merge: mergeFnPaginatedData,
					},
					myContractBilling(cached, { args: { id }, field: { selectionSet } }) {
						if (cached) {
							return cached;
						}
						const typename = `Billing`;
						const fragmentName = `AdHocBillingFragment`;

						if (Capacitor.isNativePlatform()) {
							try {
								// No need to wait for response from this promise
								debouncedWidgetUpdate();
							} catch {
								// ignore
							}
						}

						try {
							const cachedEntity = cache.readFragment({
								id: `${typename}:${id}`,
								fragment: wrapInDocument([
									selectionSetToFragment(fragmentName, typename, selectionSet),
									...AmountFragment.definitions,
									...BillingDefermentFragment.definitions,
									...BillingFragment.definitions,
								]),
								fragmentName,
							});
							if (cachedEntity) {
								return cachedEntity;
							}
						} catch (error) {
							captureException(new Error(`The Apollo Cache optimization of Billing failed.`), {
								extra: { originalError: error },
							});
						}
						return undefined;
					},
					notifications: {
						keyArgs: [`filter`],
						merge: mergeFnPaginatedData,
					},
					rewardsPartnerPromocodesOffer(cached, { args: { id } }) {
						return (
							cached ||
							cache.readFragment({
								id: `PartnerPromocodeOffer:${id}`,
								fragment: PromocodeOfferFragment,
								fragmentName: `PromocodeOfferFragment`,
							}) ||
							undefined
						);
					},
					rewardsPartnerPromocodesOffers: {
						keyArgs: [`filter`, `sort`],
						merge: mergeFnPaginatedData,
					},
					rewardsPartnerPromocodesPurchases: {
						keyArgs: [`sort`],
						merge: mergeFnPaginatedData,
					},
					partners: {
						keyArgs: [`filter`, `sort`],
						merge: mergeFnPaginatedData,
					},
					myCashbackAdvertisers: {
						keyArgs: [`filter`, `limit`],
						merge: mergeFnPaginatedData,
					},
					myCashbackOrders: {
						keyArgs: [`limit`],
						merge: mergeFnPaginatedData,
					},
				},
			},
		},
		possibleTypes: possibleTypesFromApiArtifacts(apiArtifacts),
	});

	const acceptValidationOnly =
		process.env.NODE_ENV === 'development' || process.env.TARGET_ENV === 'accept';

	const httpAgent = createAxiosHttpAgent({
		baseURL: process.env.API_BASE_URL,
		requestMiddlewares: [
			memoPendingRequestsMiddleware({ defaultTTL: 500, operationIdTTL: { myContract: 2000 } }),
			gqlXLanguageMiddleware(language),
			xFieldsRequestMiddleware,
			acceptValidationOnly &&
				// eslint-disable-next-line global-require
				oasValidationMiddleware(require(`.spec/self-care/openapi.json`)),
		].filter(Boolean),
	});

	return new ApolloClient({
		cache,
		defaultOptions: {
			watchQuery: {
				nextFetchPolicy(lastFetchPolicy) {
					// this "fixes" weird behavior of apollo hooks with fetch policies `cache-and-network` or `network-only`.
					// E.g. calling `fetchMore` with `cache-and-network` performed two request, one for `fetchMore` data plus one
					// request with initial variables, which was not only inefficient, but it also confused merge function for
					// `fetchMore` result. This converts all subsequent calls to `cache-first` which seems works more naturally.
					// @see https://github.com/apollographql/apollo-client/issues/6916#issuecomment-811203002
					return lastFetchPolicy === `cache-and-network` || lastFetchPolicy === `network-only`
						? `cache-first`
						: lastFetchPolicy;
				},
			},
		},
		link: ApolloLink.from(
			[
				getAuthorizationApolloLink(config?.accessToken),
				process.env.NODE_ENV === 'development' && apolloLogger,
				createApolloLink(cache, apiArtifacts, httpAgent),
			].filter(Boolean)
		),
		resolvers,
		ssr,
	});
};

export default createApolloClient();
